import { Typography } from "@mui/material";
import {
  CarriersTable,
  CarriersToolbar,
  buildBlacklistConfirmDescription,
  buildBlacklistSuccessDescription,
  buildUnblacklistConfirmDescription,
  buildUnblacklistSuccessDescription,
  buildCarriersDrawerFilterChips,
  getCarriersListFilterSections,
  getYardBlacklistConfirmTitle,
  isCarrierBlacklisted,
  ConfirmActionDialog,
  ListAppliedFilterChips,
  ListFiltersDrawer,
  ListTablePaginationBar,
  useCarriersListPageCore,
  useListErrorNotification,
  useNotificationSnackbar,
  useCustomersDropdown,
  useLocationsList,
  CommonDropdownGenerators,
  buildMultiSelectFilterChips,
  mergeAppliedFilterChips,
  getThunkErrorMessage,
} from "design-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { InviteCarrierDrawer } from "../components/invite-carrier-drawer.component";
import type { CarrierInviteSubmitPayload } from "../types";
import { bookingWizardCustomerOptions } from "../../../shared/utils/wizard-customer-options";
import { OPERATOR_SCOPED_ID } from "../../../shared/constants/operator.constants";
import { useBookingWizardCustomers } from "../../../shared/hooks/useBookingWizardCustomers";
import {
  carriersService,
  lookupCarrierOkForAdd,
  INVITE_CARRIER_FAILED_MESSAGE,
} from "../../../shared/services/carriers.service";
import { useAppDispatch, useAppSelector } from "../../../shared/store/hook";
import {
  CARRIERS_LIST_PAGE_SIZE,
  blacklistCarriers,
  clearCarriersListDrawerFilters,
  fetchCarriersList,
  inviteCarrier,
  removeCarriersListDrawerValue,
  setCarriersListCustomerIds,
  setCarriersListDrawerFilters,
  setCarriersListRange,
  setCarriersListSearch,
  setCarriersListShowOnlyActive,
  setCarriersListSort,
  setCarriersListToolbarLocationFilters,
} from "../../../shared/store/carriers";
import {
  selectCarriersListError,
  selectCarriersListFilterAviroStatus,
  selectCarriersListFilterDotStatus,
  selectCarriersListFilterRiskLevel,
  selectCarriersListFromIndex,
  selectCarriersListLoading,
  selectCarriersListCustomerIds,
  selectCarriersListRows,
  selectCarriersListSearch,
  selectCarriersListShowOnlyActive,
  selectCarriersListSortField,
  selectCarriersListSortOrder,
  selectCarriersListToolbarLocationFilterIds,
  selectCarriersListTotal,
} from "../../../shared/store/carriers/carriers.selector";

export const CarriersListPage = () => {
  const dispatch = useAppDispatch();
  const rows = useAppSelector(selectCarriersListRows);
  const totalCarriers = useAppSelector(selectCarriersListTotal);
  const customerIds = useAppSelector(selectCarriersListCustomerIds);
  const fromIndex = useAppSelector(selectCarriersListFromIndex);
  const loading = useAppSelector(selectCarriersListLoading);
  const error = useAppSelector(selectCarriersListError);
  const sortingOrder = useAppSelector(selectCarriersListSortOrder);
  const sortingField = useAppSelector(selectCarriersListSortField);
  const locationFilterIds = useAppSelector(
    selectCarriersListToolbarLocationFilterIds,
  );
  const appliedDot = useAppSelector(selectCarriersListFilterDotStatus);
  const appliedRisk = useAppSelector(selectCarriersListFilterRiskLevel);
  const appliedAviro = useAppSelector(selectCarriersListFilterAviroStatus);
  const showOnlyActive = useAppSelector(selectCarriersListShowOnlyActive);
  const search = useAppSelector(selectCarriersListSearch);

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filterDraft, setFilterDraft] = useState<Record<string, string[]>>({});
  const [selectedCarrierIds, setSelectedCarrierIds] = useState<string[]>([]);
  const [blacklistConfirmOpen, setBlacklistConfirmOpen] = useState(false);
  const [blacklistSubmitting, setBlacklistSubmitting] = useState(false);
  const [addCarrierOpen, setAddCarrierOpen] = useState(false);
  const [addCarrierSubmitting, setAddCarrierSubmitting] = useState(false);

  const {
    customers: addCarrierCustomers,
    loading: addCarrierCustomersLoading,
    error: addCarrierCustomersLoadError,
  } = useBookingWizardCustomers({
    enabled: addCarrierOpen,
    operatorId: OPERATOR_SCOPED_ID,
  });
  const addCarrierCustomerOptions = useMemo(
    () => bookingWizardCustomerOptions(addCarrierCustomers, { mode: "create" }),
    [addCarrierCustomers],
  );

  const { locations: locationsFromApi, loading: locationsLoading } =
    useLocationsList({
      autoFetch: true,
    });
  const { customers: customersFromApi, loading: customersLoading } =
    useCustomersDropdown(carriersService);
  const { showNotification, snackbar } = useNotificationSnackbar();

  const listPage = Math.floor(fromIndex / CARRIERS_LIST_PAGE_SIZE) + 1;
  const listFilterSections = useMemo(() => getCarriersListFilterSections(), []);

  const refetchList = useCallback(() => {
    dispatch(fetchCarriersList());
  }, [dispatch]);

  const {
    resetListWindowFirstPage,
    updateFilter,
    handlePageChange,
    handleSearchTrigger,
    handleShowOnlyActive,
    openFilterDrawerDraft,
    handleFilterApply: applyDrawerFilters,
    handleSortHeaderClick,
  } = useCarriersListPageCore({
    dispatch,
    pageSize: CARRIERS_LIST_PAGE_SIZE,
    sortingField,
    sortingOrder,
    appliedDot,
    appliedRisk,
    appliedAviro,
    setCarriersListRange,
    refetchList,
    setCarriersListSearch,
    setCarriersListSort,
    setCarriersListShowOnlyActive,
    setCarriersListDrawerFilters,
  });

  const selectedCarrierNames = useMemo(
    () =>
      rows
        .filter((row) => selectedCarrierIds.includes(row.id))
        .map((row) => row.carrierName.trim())
        .filter(Boolean),
    [rows, selectedCarrierIds],
  );

  const yardBlacklistMode = useMemo((): "blacklist" | "unblacklist" | null => {
    if (selectedCarrierIds.length === 0) return null;
    const selectedRows = selectedCarrierIds
      .map((id) => rows.find((row) => row.id === id))
      .filter((row): row is NonNullable<typeof row> => row != null);
    if (selectedRows.length !== selectedCarrierIds.length) return null;
    const allBlacklisted = selectedRows.every((row) =>
      isCarrierBlacklisted(row),
    );
    const allNonBlacklisted = selectedRows.every(
      (row) => !isCarrierBlacklisted(row),
    );
    if (allBlacklisted) return "unblacklist";
    if (allNonBlacklisted) return "blacklist";
    return null;
  }, [rows, selectedCarrierIds]);

  const yardBlacklistEnabled = yardBlacklistMode != null;
  const removeFromBlacklist = yardBlacklistMode === "unblacklist";

  const locationOptions = useMemo(
    () => CommonDropdownGenerators.locations(locationsFromApi),
    [locationsFromApi],
  );

  const customerOptions = useMemo(
    () => CommonDropdownGenerators.customers(customersFromApi?.customers),
    [customersFromApi],
  );

  const appliedChipItems = useMemo(() => {
    const drawerChips = buildCarriersDrawerFilterChips(
      appliedDot,
      appliedRisk,
      appliedAviro,
      (sectionId: string, value: string) => {
        updateFilter(removeCarriersListDrawerValue, { sectionId, value });
      },
    );

    return mergeAppliedFilterChips(
      drawerChips,
      buildMultiSelectFilterChips({
        sectionId: "customer",
        selectedIds: customerIds,
        options: customerOptions,
        onRemoveId: (id) => {
          updateFilter(
            setCarriersListCustomerIds,
            customerIds.filter((v) => v !== id),
          );
        },
      }),
      buildMultiSelectFilterChips({
        sectionId: "toolbarLocation",
        selectedIds: locationFilterIds,
        options: locationOptions,
        onRemoveId: (id) => {
          updateFilter(
            setCarriersListToolbarLocationFilters,
            locationFilterIds.filter((v) => v !== id),
          );
        },
      }),
    );
  }, [
    appliedDot,
    appliedRisk,
    appliedAviro,
    updateFilter,
    customerIds,
    customerOptions,
    locationFilterIds,
    locationOptions,
  ]);

  const openFilterDrawer = useCallback(() => {
    setFilterDraft(openFilterDrawerDraft());
    setFilterDrawerOpen(true);
  }, [openFilterDrawerDraft]);

  const handleFilterApply = useCallback(
    (applied: Record<string, string[]>) => {
      applyDrawerFilters(applied, () => setFilterDrawerOpen(false));
    },
    [applyDrawerFilters],
  );

  const handleClearAllChips = useCallback(() => {
    resetListWindowFirstPage();
    dispatch(clearCarriersListDrawerFilters());
    dispatch(setCarriersListCustomerIds([]));
    dispatch(setCarriersListToolbarLocationFilters([]));
    refetchList();
  }, [resetListWindowFirstPage, refetchList, dispatch]);

  const handleLocationsChange = useCallback(
    (ids: string[]) => {
      updateFilter(setCarriersListToolbarLocationFilters, ids);
    },
    [updateFilter],
  );

  const handleCustomersChange = useCallback(
    (ids: string[]) => {
      updateFilter(setCarriersListCustomerIds, ids);
    },
    [updateFilter],
  );

  useEffect(() => {
    dispatch(fetchCarriersList());
  }, [dispatch]);

  useListErrorNotification(error, showNotification, {
    title: "Could not load carriers",
  });

  const handleAddCarrierClick = useCallback(() => {
    setAddCarrierOpen(true);
  }, []);

  const handleAddCarrierClose = useCallback(() => {
    if (addCarrierSubmitting) return;
    setAddCarrierOpen(false);
  }, [addCarrierSubmitting]);

  const handleDotLookup = useCallback(
    async (dot: string) => lookupCarrierOkForAdd(dot),
    [],
  );

  const handleAddCarrierSubmit = useCallback(
    async (payload: CarrierInviteSubmitPayload) => {
      setAddCarrierSubmitting(true);
      try {
        const safetyScore = payload.safetyScore.trim();
        const parsedCustomerId = Number(payload.customerId);
        if (!Number.isFinite(parsedCustomerId) || parsedCustomerId <= 0) {
          showNotification({
            variant: "error",
            title: "Could not add carrier",
            description: "Select a customer to continue.",
          });
          return;
        }
        const customerId = parsedCustomerId;
        const customerName =
          addCarrierCustomerOptions.find((o) => o.value === payload.customerId)?.label ??
          undefined;

        await dispatch(
          inviteCarrier({
            customerId,
            customerName,
            carrierName: payload.carrierName,
            contactName: payload.contactName,
            email: payload.email,
            phone: payload.phone || undefined,
            scac: payload.scac || undefined,
            stateId: payload.stateProvincialId || undefined,
            businessAddress: payload.businessAddress || undefined,
            dotNumber: payload.dotNumber,
            ifCarrierHasFederalDot: payload.ifCarrierHasFederalDot,
            dotStatus: payload.dotStatus || undefined,
            riskScore: payload.riskScore || undefined,
            safetyScore:
              safetyScore && /^\d+$/.test(safetyScore)
                ? Number(safetyScore)
                : undefined,
          }),
        ).unwrap();
        setAddCarrierOpen(false);
        showNotification({
          variant: "success",
          title: "Carrier invited",
          description: `Invite sent to ${payload.carrierName}.`,
        });
      } catch (e: unknown) {
        showNotification({
          variant: "error",
          title: "Could not add carrier",
          description: getThunkErrorMessage(e, INVITE_CARRIER_FAILED_MESSAGE),
        });
      } finally {
        setAddCarrierSubmitting(false);
      }
    },
    [addCarrierCustomerOptions, dispatch, showNotification],
  );

  const openBlacklistConfirm = useCallback(() => {
    if (!yardBlacklistEnabled) return;
    setBlacklistConfirmOpen(true);
  }, [yardBlacklistEnabled]);

  const handleBlacklistConfirm = useCallback(async () => {
    const ids = selectedCarrierIds
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n));
    if (ids.length === 0 || yardBlacklistMode == null) return;

    setBlacklistSubmitting(true);
    try {
      await dispatch(
        blacklistCarriers({
          carrierIds: ids,
          action: yardBlacklistMode,
        }),
      ).unwrap();
      setSelectedCarrierIds([]);
      setBlacklistConfirmOpen(false);
      if (yardBlacklistMode === "unblacklist") {
        showNotification({
          variant: "success",
          title:
            ids.length === 1
              ? "Unblacklisted from yard"
              : "Carriers unblacklisted from yard",
          description: buildUnblacklistSuccessDescription(
            selectedCarrierNames,
            ids.length,
          ),
        });
      } else {
        showNotification({
          variant: "success",
          title:
            ids.length === 1 ? "Carrier blacklisted" : "Carriers blacklisted",
          description: buildBlacklistSuccessDescription(
            selectedCarrierNames,
            ids.length,
          ),
        });
      }
    } catch (e: unknown) {
      showNotification({
        variant: "error",
        title:
          yardBlacklistMode === "unblacklist"
            ? "Unblacklist failed"
            : "Blacklist failed",
        description: getThunkErrorMessage(
          e,
          yardBlacklistMode === "unblacklist"
            ? "Could not unblacklist carriers from yard."
            : "Could not blacklist carriers.",
        ),
      });
    } finally {
      setBlacklistSubmitting(false);
    }
  }, [
    dispatch,
    selectedCarrierIds,
    selectedCarrierNames,
    showNotification,
    yardBlacklistMode,
  ]);

  const yardBlacklistConfirmDescription =
    yardBlacklistMode === "unblacklist"
      ? buildUnblacklistConfirmDescription(
          selectedCarrierNames,
          selectedCarrierIds.length,
        )
      : buildBlacklistConfirmDescription(
          selectedCarrierNames,
          selectedCarrierIds.length,
        );

  return (
    <div className="carriers-page">
      <Typography variant="h5" className="carriers-page__title" component="h1">
        Carriers
      </Typography>
      <div className="users-page__main">
        <CarriersToolbar
          variant="operator"
          totalResults={totalCarriers}
          showBlacklistAction
          blacklistEnabled={yardBlacklistEnabled}
          removeFromBlacklist={removeFromBlacklist}
          onBlacklistClick={openBlacklistConfirm}
          onAddCarrierClick={handleAddCarrierClick}
          searchValue={search}
          onSearchTrigger={handleSearchTrigger}
          onFilterClick={openFilterDrawer}
          showOnlyActive={showOnlyActive}
          onShowOnlyActiveChange={handleShowOnlyActive}
          customerOptions={customerOptions}
          selectedCustomerIds={customerIds}
          onCustomersChange={handleCustomersChange}
          customersLoading={customersLoading}
          locationOptions={locationOptions}
          selectedLocationIds={locationFilterIds}
          onLocationsChange={handleLocationsChange}
          locationsLoading={locationsLoading}
        />
        <ListAppliedFilterChips
          items={appliedChipItems}
          onClearAll={handleClearAllChips}
          totalResults={totalCarriers}
        />
        <div className="users-page__table-scroll">
          <CarriersTable
            variant="operator"
            loading={loading}
            rows={error ? [] : rows}
            operatorSortingField={sortingField}
            operatorSortingOrder={sortingOrder}
            onOperatorSortingHeaderClick={handleSortHeaderClick}
            selectedCarrierIds={selectedCarrierIds}
            onSelectedCarrierIdsChange={setSelectedCarrierIds}
            getCarrierNameLink={(row) => ({
              to: `/carriers/${row.id}`,
            })}
            paginationFooter={
              <ListTablePaginationBar
                totalCount={totalCarriers}
                page={listPage}
                pageSize={CARRIERS_LIST_PAGE_SIZE}
                onPageChange={handlePageChange}
                disabled={loading}
              />
            }
          />
        </div>
      </div>
      <ListFiltersDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        title="Filters"
        subtitle="Select filters for carriers."
        sections={listFilterSections}
        value={filterDraft}
        onChange={setFilterDraft}
        onApply={handleFilterApply}
      />
      <ConfirmActionDialog
        open={blacklistConfirmOpen}
        onClose={() => setBlacklistConfirmOpen(false)}
        title={getYardBlacklistConfirmTitle(
          yardBlacklistMode ?? "blacklist",
          selectedCarrierIds.length,
        )}
        description={yardBlacklistConfirmDescription}
        primaryLabel={
          yardBlacklistMode === "unblacklist"
            ? "Unblacklist from yard"
            : "Blacklist"
        }
        onPrimaryClick={() => void handleBlacklistConfirm()}
        primaryDisabled={blacklistSubmitting}
        primaryLoading={blacklistSubmitting}
      />
      <InviteCarrierDrawer
        open={addCarrierOpen}
        onClose={handleAddCarrierClose}
        onDotLookup={handleDotLookup}
        onSubmit={handleAddCarrierSubmit}
        customerOptions={addCarrierCustomerOptions}
        customersLoading={addCarrierCustomersLoading}
        customersLoadError={addCarrierCustomersLoadError}
        submitting={addCarrierSubmitting}
      />
      {snackbar}
    </div>
  );
};
